/** Deal CRM payload types and formatters, shared by the board and the deal
 *  drawer. Mirrors app/schemas/deal_pipeline.py. */

export interface DealBuyer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface DealCardListing {
  listing_id: string;
  title: string | null;
  reference_code: string | null;
  industry_category: string | null;
  asking_price: number | null;
  /** Annual revenue. Summed per column in the stage headers. */
  revenue: number | null;
  make_offer: boolean;
  image_url: string | null;
}

export interface DealAdvisor {
  id: string;
  name: string;
}

export type DealOutcome = "won" | "lost";

export interface DealCard {
  card_id: string | null;
  stage: string;
  position: number;
  /** Null on a listing-only deal an advisor added before any buyer surfaced. */
  buyer: DealBuyer | null;
  listing: DealCardListing;
  advisor: DealAdvisor | null;
  /** Favorites only ever appear on the buyer's own board. */
  source: "interest" | "favorite" | "listing";
  /** The buyer withdrew — staff can close it out but not move it. */
  withdrawn: boolean;
  outcome: DealOutcome | null;
  closed_at: string | null;
  offer_amount: number | null;
  target_close_date: string | null;
  next_step: string | null;
  next_step_due: string | null;
  updated_at: string | null;
  /** Notes the caller can read — staff notes for staff, own notes for a buyer. */
  note_count: number;
}

export interface StageSummary {
  count: number;
  revenue: number;
}

export interface BoardSummary {
  total: number;
  total_value: number;
  valued_listings: number;
  stages_in_play: number;
  late_stage: number;
}

export interface BoardResponse {
  can_edit: boolean;
  can_note: boolean;
  is_staff: boolean;
  cards: DealCard[];
  stages: Record<string, StageSummary>;
  summary: BoardSummary;
  limit_per_stage: number;
  advisors: DealAdvisor[];
}

/** May the caller drag / step this card? Closed and withdrawn deals are
 *  frozen until reopened (or, for withdrawn, closed out). */
export const isMovable = (canEdit: boolean, c: DealCard) =>
  canEdit && !c.withdrawn && c.outcome === null;

/** A buyer's deal is identified by (buyer, listing) — stable whether or not
 *  the card has been persisted yet, so an optimistic move still matches after
 *  the first save creates the row. A listing-only deal has no buyer to key on
 *  and always has a stored row, so its id serves. */
export const dealKey = (c: DealCard) =>
  c.buyer ? `${c.buyer.id}:${c.listing.listing_id}` : `card:${c.card_id}`;

/** The identity every deal endpoint takes. */
export const dealTarget = (c: DealCard) => ({
  listing_id: c.listing.listing_id,
  buyer_id: c.buyer?.id ?? null,
  card_id: c.card_id,
});

export function formatShortPrice(card: DealCardListing): string {
  if (card.make_offer && card.asking_price == null) return "Make Offer";
  const p = card.asking_price;
  if (p == null) return "—";
  return fmtTotal(p);
}

/** Compact money for the KPI tiles and stage headers. A whole book of business
 *  adds up past a billion, so the ramp does not stop at millions — without the
 *  B step a $2.4B pipeline reads as "$2400.0M". */
export function fmtTotal(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString()}`;
}

/** Exact dollars for the tooltip behind a compacted figure. */
export const fmtExact = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** A DATE column ("2026-10-14") as a short local date. Parsed as a calendar
 *  date, not as UTC midnight — new Date("2026-10-14") lands on the 13th for
 *  anyone west of Greenwich. */
export function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** True when a DATE column is before today (local). */
export function isOverdue(iso: string | null): boolean {
  if (!iso) return false;
  const [y, m, d] = iso.split("-").map(Number);
  const due = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}
